// Security review: route workflow signing through the wallet identity/race guards.
import { useRef, useState } from 'react';
import {
  Background,
  Controls,
  ReactFlow,
  ReactFlowProvider,
  Handle,
  Position,
} from '@xyflow/react';

import { Api, subscribeToRun } from '../lib/api.js';
import { useApi, useWallet } from '../lib/wallet.jsx';
import { Badge, Button, Card, Empty, ErrorState, Field, Hash, Modal, Skeleton } from '../components/ui.jsx';

const STATUS_TONE = { active: 'success', paused: 'pending', draft: 'neutral', revoked: 'danger', expired: 'danger' };

/** A labelled pipeline node for the workflow graph. */
function StageNode({ data }) {
  return (
    <div className={`wfgraph__node wfgraph__node--${data.kind} ${data.live ? 'wfgraph__node--live' : ''}`}>
      {data.hasTarget && <Handle type="target" position={Position.Left} />}
      <span className="wfgraph__node-kind">{data.tag}</span>
      <span className="wfgraph__node-title">{data.title}</span>
      {data.subtitle && <span className="wfgraph__node-sub">{data.subtitle}</span>}
      {data.hasSource && <Handle type="source" position={Position.Right} />}
    </div>
  );
}

const WF_NODE_TYPES = { stage: StageNode };

/**
 * n8n-style read-only pipeline for a deployed workflow: what fires it, which
 * agent runs, and what it produces. `phase` highlights the active stage while a
 * run is executing so the graph animates like n8n's run view.
 */
function WorkflowGraph({ workflow, phase }) {
  const nodes = [
    {
      id: 'trigger',
      type: 'stage',
      position: { x: 0, y: 40 },
      data: {
        kind: 'trigger',
        tag: 'Trigger',
        title: 'Schedule',
        subtitle: workflow.schedule,
        hasSource: true,
        live: phase === 'trigger',
      },
    },
    {
      id: 'agent',
      type: 'stage',
      position: { x: 240, y: 40 },
      data: {
        kind: 'agent',
        tag: 'Agent',
        title: workflow.agentName ?? 'Agent',
        subtitle: workflow.instructionText ? 'Runs the purpose' : 'No instruction',
        hasTarget: true,
        hasSource: true,
        live: phase === 'agent',
      },
    },
    {
      id: 'output',
      type: 'stage',
      position: { x: 480, y: 40 },
      data: {
        kind: 'output',
        tag: 'Output',
        title: 'Deliverable + proof',
        subtitle: 'Attested run',
        hasTarget: true,
        live: phase === 'output',
      },
    },
  ];
  const edges = [
    { id: 'e1', source: 'trigger', target: 'agent', animated: phase != null },
    { id: 'e2', source: 'agent', target: 'output', animated: phase === 'agent' || phase === 'output' },
  ];

  return (
    <div className="wfgraph">
      <ReactFlowProvider>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          nodeTypes={WF_NODE_TYPES}
          fitView
          nodesDraggable={false}
          nodesConnectable={false}
          elementsSelectable={false}
          panOnDrag={false}
          zoomOnScroll={false}
          zoomOnPinch={false}
          zoomOnDoubleClick={false}
          proOptions={{ hideAttribution: true }}
          style={{ background: 'transparent' }}
        >
          <Background color="#2A2336" gap={18} />
        </ReactFlow>
      </ReactFlowProvider>
    </div>
  );
}


/**
 * Recurring workflows.
 *
 * A production feature, not a demo (§10). The screen is explicit that each
 * scheduled execution creates its own run and its own attestation, because a
 * user authorising unattended activity is entitled to know exactly what it will
 * do on chain.
 */
export function Workflows({ pushToast }) {
  const { sessionReady, isAuthenticated } = useWallet();
  const { data, loading, error, reload } = useApi(() => Api.workflows(), [sessionReady], { skip: !sessionReady });
  const { data: roster } = useApi(() => Api.agents(), [sessionReady], { skip: !sessionReady });
  const [creating, setCreating] = useState(false);

  if (error) return <ErrorState error={error} onRetry={reload} context="Workflows could not be loaded" />;
  if (!sessionReady || loading || !data) return <Skeleton rows={4} />;

  const agents = (roster?.agents ?? []).filter((a) => a.status !== 'archived');

  return (
    <div className="stack">
      <header className="page-header">
        <div>
          <h1 className="page-title">Recurring workflows</h1>
          <p className="page-subtitle">
            Scheduled work you authorise once. Every execution that actually runs creates its own attestation.
          </p>
          {!isAuthenticated && (
            <p className="page-subtitle text-muted">
              You can create and edit workflows off chain. Connect a wallet to sign the authorisation that lets one run.
            </p>
          )}
        </div>
        <Button variant="primary" onClick={() => setCreating(true)} disabled={agents.length === 0}>
          New workflow
        </Button>
      </header>

      {(() => {
        const drafts = data.workflows.filter((w) => w.status === 'draft').length;
        return drafts > 0 ? (
          <div className="workflow-notice" role="status">
            <strong>
              {drafts} {drafts === 1 ? 'workflow is' : 'workflows are'} waiting for your authorisation.
            </strong>{' '}
            Nothing runs until you sign. Press Authorise on each draft below and approve the signature in your wallet.
          </div>
        ) : null;
      })()}

      {data.workflows.length === 0 ? (
        <Empty
          title="No workflows yet"
          description="A workflow runs an agent on a schedule you control, under an authorisation you sign and can revoke at any time."
          action={agents.length > 0 && <Button variant="primary" onClick={() => setCreating(true)}>Create a workflow</Button>}
        />
      ) : (
        <div className="stack">
          {[...data.workflows]
            .sort((a, b) => (a.status === 'draft' ? 0 : 1) - (b.status === 'draft' ? 0 : 1))
            .map((w) => (
            <WorkflowRow key={w.id} workflow={w} onChange={reload} pushToast={pushToast} />
          ))}
        </div>
      )}

      <CreateWorkflowModal
        open={creating}
        agents={agents}
        onClose={() => setCreating(false)}
        onCreated={() => {
          setCreating(false);
          reload();
        }}
        pushToast={pushToast}
      />
    </div>
  );
}

function WorkflowRow({ workflow, onChange, pushToast }) {
  const { signWorkflowAuthorization, isAuthenticated, promptConnect } = useWallet();
  const [busy, setBusy] = useState(null);
  const [confirmRevoke, setConfirmRevoke] = useState(false);
  const [runs, setRuns] = useState(null);
  const [runPhase, setRunPhase] = useState(null);
  const runRequestKey = useRef(null);

  const PROOF_TO_PHASE = (proofState, status) => {
    if (status === 'terminal' || status === 'awaiting_approval') return 'output';
    if (proofState === 'Executing' || proofState === 'Queued') return 'agent';
    if (proofState && proofState.startsWith('Evidence')) return 'output';
    return 'agent';
  };

  /** Owner-triggered execution, then live-track it on the graph. */
  const runNow = async () => {
    setBusy('run');
    setRunPhase('trigger');
    try {
      runRequestKey.current ??= crypto.randomUUID();
      const res = await Api.runWorkflowNow(workflow.id, runRequestKey.current);
      runRequestKey.current = null;
      setRunPhase('agent');
      const stop = subscribeToRun(res.runId, (event, payload) => {
        if (event === 'proof.state') setRunPhase(PROOF_TO_PHASE(payload.proofState, payload.status));
        if (event === 'run.terminal') {
          setRunPhase('output');
          pushToast({ tone: 'success', message: 'Workflow run finished. See run history for the deliverable.' });
          setTimeout(() => setRunPhase(null), 4000);
          stop();
          loadRuns();
          onChange();
        }
        if (event === 'error') { stop(); setRunPhase(null); }
      });
    } catch (err) {
      setRunPhase(null);
      pushToast({ tone: 'danger', message: err.message });
    } finally {
      setBusy(null);
    }
  };

  const act = async (kind, fn, message) => {
    setBusy(kind);
    try {
      await fn();
      pushToast({ tone: 'success', message });
      onChange();
    } catch (err) {
      pushToast({ tone: 'danger', message: err.message });
    } finally {
      setBusy(null);
      setConfirmRevoke(false);
    }
  };

  /** The owner signs the authorisation in the browser; the API verifies it. */
  const authorize = () =>
    act(
      'authorize',
      async () => {
        await signWorkflowAuthorization(workflow.id);
      },
      'Workflow authorised. It will run on its schedule until you pause or revoke it.',
    );

  const loadRuns = async () => {
    try {
      const r = await Api.workflowRuns(workflow.id);
      setRuns(r.runs);
    } catch (err) {
      pushToast({ tone: 'danger', message: `Run history could not be loaded: ${err.message}` });
    }
  };

  return (
    <Card className="workflow">
      <div className="workflow__head">
        <div>
          <h2 className="workflow__name">{workflow.name}</h2>
          <p className="workflow__meta">
            {workflow.agentName ?? 'Agent'} · <span className="mono">{workflow.schedule}</span> · {workflow.timezone}
          </p>
        </div>
        <div className="workflow__badges">
          <Badge tone={workflow.status === 'draft' ? 'pending' : STATUS_TONE[workflow.status] ?? 'neutral'}>
            {workflow.status === 'draft' ? 'Needs authorisation' : workflow.status}
          </Badge>
          {workflow.autoCheckIn && <Badge tone="neutral">Auto check-in</Badge>}
        </div>
      </div>

      <dl className="facts">
        <div className="facts__row">
          <dt className="facts__label">Workflow hash</dt>
          <dd className="facts__value"><Hash value={workflow.workflowIdHash} label="workflow hash" /></dd>
        </div>
        <div className="facts__row">
          <dt className="facts__label">Runs per day</dt>
          <dd className="facts__value mono">{workflow.maxRunsPerDay}</dd>
        </div>
        <div className="facts__row">
          <dt className="facts__label">Allowed tools</dt>
          <dd className="facts__value mono">{workflow.allowedToolIds.join(', ') || 'none'}</dd>
        </div>
        <div className="facts__row">
          <dt className="facts__label">Expires</dt>
          <dd className="facts__value">{workflow.validUntil ? new Date(workflow.validUntil).toLocaleDateString() : '—'}</dd>
        </div>
        <div className="facts__row">
          <dt className="facts__label">Executions</dt>
          <dd className="facts__value mono">{workflow.occurrenceCount}</dd>
        </div>
        {workflow.instructionText && (
          <div className="facts__row">
            <dt className="facts__label">Purpose</dt>
            <dd className="facts__value">{workflow.instructionText}</dd>
          </div>
        )}
      </dl>

      {workflow.status === 'draft' && (
        <p className="workflow__explain">
          Not yet authorised. Signing binds this agent, schedule, tool list and budget. Each execution that starts
          will create one run and one on-chain attestation.
        </p>
      )}
      {workflow.status === 'revoked' && (
        <p className="workflow__explain">
          Revoked. The signature was invalidated and no future occurrence can execute. Past runs and their proofs are
          retained.
        </p>
      )}

      {workflow.status !== 'draft' && <WorkflowGraph workflow={workflow} phase={runPhase} />}

      <div className="workflow__actions">
        {workflow.status === 'draft' && (
          <Button variant="primary" loading={busy === 'authorize'} onClick={isAuthenticated ? authorize : promptConnect}>
            {isAuthenticated ? 'Authorise' : 'Connect wallet to authorise'}
          </Button>
        )}
        {workflow.status === 'active' && (
          <Button variant="primary" loading={busy === 'run'} onClick={runNow} disabled={!workflow.instructionText}>
            {runPhase ? 'Running…' : 'Run now'}
          </Button>
        )}
        {workflow.status === 'active' && (
          <Button variant="secondary" loading={busy === 'pause'} onClick={() => act('pause', () => Api.pauseWorkflow(workflow.id), 'Workflow paused.')}>
            Pause
          </Button>
        )}
        {workflow.status === 'paused' && (
          <Button variant="secondary" loading={busy === 'resume'} onClick={() => act('resume', () => Api.resumeWorkflow(workflow.id), 'Workflow resumed.')}>
            Resume
          </Button>
        )}
        {workflow.status !== 'revoked' && (
          <Button variant="ghost" onClick={() => setConfirmRevoke(true)}>Revoke</Button>
        )}
        <Button variant="ghost" onClick={loadRuns}>Run history</Button>
      </div>

      {runs && (
        <div className="workflow__runs">
          {runs.length === 0 ? (
            <p className="text-muted">No executions yet.</p>
          ) : (
            <table className="table">
              <thead>
                <tr><th>Scheduled</th><th>Outcome</th><th>Proof</th></tr>
              </thead>
              <tbody>
                {runs.map((r, i) => (
                  <tr key={r.id ?? i}>
                    <td>{new Date(r.scheduled_for).toLocaleString()}</td>
                    <td>{r.terminal_outcome ?? '—'}</td>
                    <td>
                      <Badge tone={r.proof_state === 'Confirmed and indexed' ? 'success' : 'pending'}>
                        {r.proof_state ?? 'not started'}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      <Modal
        open={confirmRevoke}
        title="Revoke this workflow"
        onClose={() => setConfirmRevoke(false)}
        footer={
          <>
            <Button variant="ghost" onClick={() => setConfirmRevoke(false)}>Cancel</Button>
            <Button variant="danger" loading={busy === 'revoke'} onClick={() => act('revoke', () => Api.revokeWorkflow(workflow.id), 'Workflow revoked.')}>
              Revoke permanently
            </Button>
          </>
        }
      >
        <p>Revocation takes effect immediately. No occurrence that has not already started will execute.</p>
        <p className="text-muted">
          Your signature is invalidated and cannot be replayed. Past runs, their evidence and their on-chain proofs
          are retained. To run this workflow again you would sign a new authorisation.
        </p>
      </Modal>
    </Card>
  );
}

function CreateWorkflowModal({ open, agents, onClose, onCreated, pushToast }) {
  const [form, setForm] = useState({ agentId: '', name: '', schedule: '0 7 * * *', timezone: 'UTC', instruction: '', maxRunsPerDay: 1, autoCheckIn: true });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await Api.createWorkflow({ ...form, maxRunsPerDay: Number(form.maxRunsPerDay) });
      pushToast({ tone: 'success', message: 'Draft workflow created. Sign its authorisation to activate it.' });
      onCreated();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const timezones = ['UTC', 'Europe/London', 'Europe/Berlin', 'America/New_York', 'America/Los_Angeles', 'Asia/Singapore', 'Asia/Dubai'];

  return (
    <Modal
      open={open}
      title="New recurring workflow"
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={busy} onClick={submit} disabled={!form.agentId || !form.name}>Create draft</Button>
        </>
      }
    >
      <Field label="Agent" htmlFor="wf-agent" hint="A workflow belongs to exactly one specialist.">
        <select id="wf-agent" className="input" value={form.agentId} onChange={set('agentId')}>
          <option value="">Select an agent</option>
          {agents.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
        </select>
      </Field>
      <Field label="Name" htmlFor="wf-name">
        <input id="wf-name" className="input" value={form.name} onChange={set('name')} maxLength={200} />
      </Field>
      <Field label="Instruction" htmlFor="wf-instruction" hint="Stored encrypted. Never placed on chain.">
        <textarea id="wf-instruction" className="input" rows={3} value={form.instruction} onChange={set('instruction')} />
      </Field>
      <div className="field-row">
        <Field label="Schedule" htmlFor="wf-schedule" hint="Cron expression.">
          <input id="wf-schedule" className="input mono" value={form.schedule} onChange={set('schedule')} />
        </Field>
        <Field label="Timezone" htmlFor="wf-tz">
          <select id="wf-tz" className="input" value={form.timezone} onChange={set('timezone')}>
            {timezones.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </Field>
      </div>
      <Field label="Maximum runs per day" htmlFor="wf-max" hint="A hard ceiling enforced by the scheduler.">
        <input id="wf-max" className="input" type="number" min={1} max={24} value={form.maxRunsPerDay} onChange={set('maxRunsPerDay')} />
      </Field>
      <label className="checkbox">
        <input type="checkbox" checked={form.autoCheckIn} onChange={set('autoCheckIn')} />
        <span>Allow this workflow to check the agent in for the day if it has not already</span>
      </label>

      <div className="notice">
        <p className="notice__title">What this will do on chain</p>
        <p className="notice__body">
          Each execution that actually starts creates one run and one attestation transaction. Executions are never
          batched together. You pay opBNB gas for each. Nothing runs until you sign the authorisation, and revoking
          it stops future executions immediately.
        </p>
      </div>
      {error && <p className="field__error" role="alert">{error}</p>}
    </Modal>
  );
}
